import React, { useEffect, useState, useCallback } from "react";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import { Plus, Trash2, ShieldQuestion, GitBranch, FlaskConical, History, EyeOff, Eye, AlertTriangle, ScrollText } from "lucide-react";

const GOVERNED_KINDS = [
  { k: "people_directory", label: "Beneficiaries & Executors" },
  { k: "digital_assets", label: "Digital Assets" },
  { k: "physical_assets", label: "Physical Assets" },
  { k: "legacy_messages", label: "Legacy Messages" },
  { k: "healthcare_directives", label: "Healthcare Directives" },
  { k: "final_wishes", label: "Final Wishes" },
  { k: "emergency_contacts", label: "Emergency Contacts" },
  { k: "family_office_items", label: "Family Office Items" },
  { k: "governance_records", label: "Governance Records" },
  { k: "succession_plans", label: "Succession Plans" },
  { k: "entities_hub", label: "Entities (Hub)" },
];

const PRIORITY_CHIP = { Low: "chip-gray", Medium: "chip-gold", High: "chip-amber", Critical: "chip-amber" };
const STATUS_CHIP = { Open: "chip-amber", "Under Review": "chip-gold", Mediation: "chip-gold", Resolved: "chip-green", Escalated: "chip-amber", Withdrawn: "chip-gray" };

async function loadRecords(kind) {
  if (!kind) return [];
  if (kind === "entities_hub") {
    const r = await A.entityHub.list();
    return r.data.map((x) => ({ id: x.id, label: x.legal_name || x.id }));
  }
  const r = await A.legacy.list(kind);
  return r.data.map((x) => ({ id: x.id, label: x.Name || x.Title || x.AccountName || x.AssetName || x.DocumentType || x.id }));
}

// ==================== SAFE SIMULATION + RECORD PROVENANCE ====================
export function SafetyProvenancePage() {
  const [kind, setKind] = useState("");
  const [records, setRecords] = useState([]);
  const [rid, setRid] = useState("");
  const [prov, setProv] = useState(null);
  const [sim, setSim] = useState(null);

  useEffect(() => {
    setRecords([]); setRid(""); setProv(null); setSim(null);
    if (kind) loadRecords(kind).then(setRecords).catch(() => setRecords([]));
  }, [kind]);

  const loadProv = useCallback(async (id) => {
    setSim(null);
    try { const r = await A.mp4.provenance(kind, id); setProv(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load provenance"); setProv(null); }
  }, [kind]);

  useEffect(() => { if (kind && rid) loadProv(rid); }, [rid, kind, loadProv]);

  const toggleAi = async () => {
    try { await A.mp4.excludeFromAi(kind, rid, !prov.exclude_from_ai); toast.success("Updated"); loadProv(rid); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const simulate = async (action) => {
    try { const r = await A.mp4.simulate(action, kind, rid); setSim(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
  };

  return (
    <div data-testid="safety-provenance-page">
      <H1>Safe Simulation & Provenance</H1>
      <Sub>Preview what an action would do before you commit it, and inspect any record's full provenance — who created it, its change history, verification, and whether the AI assistant may read it. Simulations never change data.</Sub>

      <Card className="mb-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="label-caps">Record type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="prov-kind">
              <option value="">— choose —</option>
              {GOVERNED_KINDS.map((g) => <option key={g.k} value={g.k}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Record</label>
            <select value={rid} onChange={(e) => setRid(e.target.value)} disabled={!records.length} data-testid="prov-record">
              <option value="">{records.length ? "— choose —" : "no records"}</option>
              {records.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {prov && (
        <>
          <Card className="mb-4" data-testid="provenance-panel">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="font-display text-xl flex items-center gap-2" style={{ color: "var(--primary)" }}><GitBranch size={18} /> {prov.label}</div>
              <button className="btn-outline text-xs flex items-center gap-1" onClick={toggleAi} data-testid="toggle-exclude-ai">
                {prov.exclude_from_ai ? <><Eye size={13} /> Allow AI</> : <><EyeOff size={13} /> Exclude from AI</>}
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              <Field l="Created by" v={prov.created_by} />
              <Field l="Created at" v={(prov.created_at || "").slice(0, 16).replace("T", " ")} />
              <Field l="Last updated" v={prov.updated_at ? `${(prov.updated_at || "").slice(0, 16).replace("T", " ")} by ${prov.updated_by || "—"}` : "—"} />
              <Field l="Verification" v={prov.verification} />
              <Field l="Source" v={prov.source} />
              <Field l="Status" v={prov.status} />
              <Field l="Visibility" v={prov.visibility} />
              <Field l="Shared with" v={(prov.shared_with || []).join(", ") || "—"} />
              <Field l="AI access" v={prov.exclude_from_ai ? "Excluded 🔒" : "Allowed"} />
            </div>
            {prov.open_disputes > 0 && <div className="banner-warning mt-3 text-sm flex items-center gap-2"><AlertTriangle size={14} /> {prov.open_disputes} open dispute(s) reference this record.</div>}
            <div className="mt-4">
              <div className="label-caps mb-2 flex items-center gap-1"><History size={13} /> Change history</div>
              {(prov.history || []).length === 0 && <div className="text-sm" style={{ color: "var(--muted)" }}>No recorded activity.</div>}
              <div className="space-y-1">
                {(prov.history || []).map((h, i) => (
                  <div key={i} className="text-xs border-b last:border-b-0 py-1" style={{ borderColor: "var(--border)" }}>
                    <span className="font-mono">{(h.timestamp || "").slice(0, 16).replace("T", " ")}</span> · {h.actor} — {h.action}
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card data-testid="simulation-panel">
            <div className="label-caps mb-2 flex items-center gap-1"><FlaskConical size={13} /> Safe simulation (dry-run)</div>
            <div className="flex gap-2 flex-wrap">
              <button className="btn-outline text-sm" onClick={() => simulate("delete")} data-testid="sim-delete">Simulate delete</button>
              {kind === "legacy_messages" && <button className="btn-outline text-sm" onClick={() => simulate("release_message")} data-testid="sim-release">Simulate release</button>}
            </div>
            {sim && (
              <div className="mt-3 p-3 rounded text-sm" style={{ background: "var(--surface-2)" }} data-testid="sim-result">
                <div className="font-medium mb-1">If you {sim.action.replace("_", " ")} "{sim.record}":</div>
                <ul className="list-disc pl-5">{(sim.effects || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
                {(sim.warnings || []).length > 0 && <div className="mt-2" style={{ color: "var(--overdue)" }}><ul className="list-disc pl-5">{sim.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}</ul></div>}
                <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{sim.note}</div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

const Field = ({ l, v }) => (
  <div><div className="label-caps">{l}</div><div className="mt-0.5">{v || "—"}</div></div>
);

// ==================== CONFLICT / DISPUTE REGISTER ====================
export function DisputesPage() {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ statuses: [], priorities: [] });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [r, m] = await Promise.all([A.mp4.disputes(), A.mp4.disputeMeta()]);
    setRows(r.data); setMeta(m.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const blank = () => ({ title: "", description: "", parties: "", priority: "Medium", status: "Open", related_kind: "", related_record_id: "", resolution: "" });
  const save = async () => {
    const payload = { ...editing, parties: (editing.parties || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing.id) await A.mp4.patchDispute(editing.id, payload);
      else await A.mp4.createDispute(payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete this dispute?")) { try { await A.mp4.delDispute(id); load(); } catch (e) { toast.error("Failed"); } } };
  const setStatus = async (id, status) => { try { await A.mp4.patchDispute(id, { status }); load(); } catch (e) { toast.error("Failed"); } };

  if (!rows) return <div>Loading…</div>;
  return (
    <div data-testid="disputes-page">
      <H1>Conflict & Dispute Register</H1>
      <Sub>Log and track family, trustee, beneficiary, or vendor disputes with parties, priority, and resolution status. Link a dispute to any record so it surfaces on that record's provenance panel.</Sub>
      <div className="flex justify-between items-center mb-3">
        <H2>{rows.length} dispute{rows.length === 1 ? "" : "s"}</H2>
        <button className="btn-gold flex items-center gap-1" onClick={() => setEditing(blank())} data-testid="dispute-new"><Plus size={14} /> New dispute</button>
      </div>

      {editing && (
        <Card className="mb-4" data-testid="dispute-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label-caps">Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="dispute-title" /></div>
            <div><label className="label-caps">Parties (comma separated)</label><input value={editing.parties} onChange={(e) => setEditing({ ...editing, parties: e.target.value })} data-testid="dispute-parties" /></div>
            <div><label className="label-caps">Priority</label><select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} data-testid="dispute-priority">{meta.priorities.map((p) => <option key={p}>{p}</option>)}</select></div>
            <div><label className="label-caps">Status</label><select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="dispute-status">{meta.statuses.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div><label className="label-caps">Related record type (optional)</label><select value={editing.related_kind} onChange={(e) => setEditing({ ...editing, related_kind: e.target.value })}><option value="">—</option>{GOVERNED_KINDS.map((g) => <option key={g.k} value={g.k}>{g.label}</option>)}</select></div>
            <div><label className="label-caps">Related record id (optional)</label><input value={editing.related_record_id} onChange={(e) => setEditing({ ...editing, related_record_id: e.target.value })} /></div>
            <div className="md:col-span-2"><label className="label-caps">Description</label><textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="dispute-description" /></div>
            <div className="md:col-span-2"><label className="label-caps">Resolution / notes</label><textarea rows={2} value={editing.resolution} onChange={(e) => setEditing({ ...editing, resolution: e.target.value })} /></div>
          </div>
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={save} data-testid="dispute-save">Save</button><button className="btn-outline" onClick={() => setEditing(null)}>Cancel</button></div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((d) => (
          <Card key={d.id} data-testid={`dispute-row-${d.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium flex items-center gap-2" style={{ color: "var(--primary)" }}>
                  <ShieldQuestion size={15} /> {d.title}
                  <span className={`chip text-xs ${STATUS_CHIP[d.status] || "chip-gray"}`}>{d.status}</span>
                  <span className={`chip text-xs ${PRIORITY_CHIP[d.priority] || "chip-gray"}`}>{d.priority}</span>
                </div>
                {d.description && <div className="text-sm mt-1">{d.description}</div>}
                <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                  {(d.parties || []).length ? `Parties: ${d.parties.join(", ")}` : ""}
                  {d.related_record_id ? ` · linked to ${d.related_kind}/${d.related_record_id.slice(0, 8)}` : ""}
                </div>
                {d.resolution && <div className="text-xs mt-1 italic" style={{ color: "var(--muted)" }}>Resolution: {d.resolution}</div>}
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end items-center">
                <select value={d.status} onChange={(e) => setStatus(d.id, e.target.value)} className="max-w-[140px] text-xs" data-testid={`dispute-setstatus-${d.id}`}>{meta.statuses.map((s) => <option key={s}>{s}</option>)}</select>
                <button className="btn-outline text-xs" onClick={() => setEditing({ ...d, parties: (d.parties || []).join(", ") })} data-testid={`dispute-edit-${d.id}`}>Edit</button>
                <button className="btn-outline text-xs" style={{ color: "var(--overdue)" }} onClick={() => del(d.id)}><Trash2 size={12} /></button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card><div className="text-sm" style={{ color: "var(--muted)" }}>No disputes recorded. A clean register is a good sign.</div></Card>}
      </div>
    </div>
  );
}
